import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, Icon, LinearProgress, MenuItem, Modal, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import { VForm, VTextField, useVForm } from "../../../shared/forms";
import { FinancialService, ICancelSetup, ISetup } from "../../../shared/services/api/financial/FinancialService";
import moment from "moment";

type CancelSetupFormData = {
	reason?: string;
	note?: string;
};

interface IModalCancelInvocieProps {
	id: string;
	clientName: string;
	dueDate: string;
	valueSetup: string;
	isOpenModal: boolean;
	isCloseModal?: () => void;
	widthModfy?: string;
	heightModfy?: string;
	onclickConfirm?: () => void;
	onCloseModal?: (event?: {}, reason?: "backdropClick" | "escapeKeyDown") => void;
	selectedSetups?: ISetup[];
}

export const ModalCancelSetup: React.FC<IModalCancelInvocieProps> = ({
	id,
	clientName,
	dueDate,
	valueSetup,
	isCloseModal,
	isOpenModal,
	onCloseModal,
	widthModfy,
	heightModfy,
	onclickConfirm,
	selectedSetups,
}) => {
	const defaultReason = "Outros";
	const [isLoading, setIsLoading] = useState(false);
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [note, setNote] = useState("");
	const [reason, setReason] = useState(defaultReason);
	const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
	const [processedCount, setProcessedCount] = useState(0);
	const [processingSummary, setProcessingSummary] = useState<{ success: number; error: number } | null>(null);

	const setupsToProcess = useMemo(
		() =>
			selectedSetups && selectedSetups.length > 0
				? selectedSetups
				: [
					{
						id,
						tenant_name: clientName,
						dueDate,
						raw_amount: valueSetup,
					} as ISetup,
				  ],
		[selectedSetups, id, clientName, dueDate, valueSetup]
	);
	const isBatchMode = setupsToProcess.length > 1;
	const currentSetup = setupsToProcess[Math.min(currentProcessingIndex, setupsToProcess.length - 1)];

	const style = {
		position: "absolute" as "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: widthModfy?.trim() ? widthModfy : "auto",
		height: heightModfy?.trim() ? heightModfy : "auto",
		minWidth: "650px",
		bgcolor: "background.paper",
		border: "1px solid #605e5e",
		boxShadow: 24,
		p: 4,
		borderRadius: "6px",
	};

	useEffect(() => {
		if (!isOpenModal) return;

		setNote("");
		setReason(defaultReason);
		setCurrentProcessingIndex(0);
		setProcessedCount(0);
		setProcessingSummary(null);
		formRef.current?.setData({ reason: defaultReason, note: "" });
	}, [isOpenModal, formRef]);

	const handleCancelSetup = async (dados: CancelSetupFormData) => {
		setIsLoading(true);
		setProcessedCount(0);
		setProcessingSummary(null);

		let successCount = 0;
		let errorCount = 0;
		const totalItems = setupsToProcess.length;

		for (let index = 0; index < totalItems; index++) {
			const setup = setupsToProcess[index];
			setCurrentProcessingIndex(index);

			const payload: ICancelSetup = {
				reason,
				note,
			};

			const result = await FinancialService.CancelSetup(setup.id, payload);
			if (result instanceof Error) {
				errorCount++;
			} else {
				successCount++;
			}

			setProcessedCount(index + 1);

			if (index < totalItems - 1) {
				await new Promise((resolve) => setTimeout(resolve, 400));
			}
		}

		setIsLoading(false);
		setProcessingSummary({ success: successCount, error: errorCount });

		toastHandler.present({
			type: errorCount === 0 ? "success" : successCount > 0 ? "warning" : "error",
			position: "top-right",
			messageOrContent: (
				<ToastContent
					title={"Processamento concluído"}
					content={`Sucesso: ${successCount} | Erros: ${errorCount}`}
				/>
			),
		});

		onclickConfirm?.();
		isCloseModal?.();
	};

	return (
		<div>
			<Modal
				open={isOpenModal}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				onClose={(event, reasonClose) => {
					if (isLoading) return;
					onCloseModal?.(event, reasonClose);
					isCloseModal?.();
				}}
			>
				<Box sx={style}>
					<DialogTitle style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
						<Typography id="modal-modal-title" variant="h5" color={"primary"} marginBottom={3}>
							{isBatchMode ? `Cancelamento em lote (${setupsToProcess.length})` : "Cancelamento de cobraça de taxa"}
						</Typography>
						{isBatchMode && (
							<Typography variant="body2" color="text.secondary" marginBottom={2}>
								O motivo e a descrição informados serão replicados para todas as linhas selecionadas.
							</Typography>
						)}
					</DialogTitle>
					<DialogContent style={{ display: "flex", justifyContent: "center", padding: "2px", margin: 0 }}>
						<Box sx={{ width: "100%" }}>
							<Typography>
								<VForm
									id="formIdCancelSetup"
									placeholder="Your placeholder"
									onPointerEnterCapture={() => console.log("Pointer entered")}
									onPointerLeaveCapture={() => console.log("Pointer left")}
									ref={formRef}
									onSubmit={handleCancelSetup}
								>
									<Box className="teste" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
										{isLoading && (
											<Grid item>
												<Typography variant="body2" color="primary" sx={{ mb: 1 }}>
													Processando {processedCount + 1} de {setupsToProcess.length}: {currentSetup?.tenant_name || clientName}
												</Typography>
												<LinearProgress variant="indeterminate" />
											</Grid>
										)}
										{processingSummary && !isLoading && (
											<Typography variant="body2" color="text.secondary">
												Último processamento: {processingSummary.success} sucesso(s) e {processingSummary.error} erro(s).
											</Typography>
										)}

										<Grid container item direction="row" spacing={1} sx={{ mb: 0 }}>
											<Grid item xs={2} sx={{ textAlign: "left" }}>
												Cliente :
											</Grid>
											<Grid item xs={8} sx={{ alignItems: "left" }}>
												<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
													{currentSetup?.tenant_name ?? clientName}
												</Typography>
											</Grid>
										</Grid>
										<Grid container item direction="row" spacing={1} sx={{ mb: 2 }}>
											<Grid item xs={2} sx={{ textAlign: "left" }}>
												Vencimento :
											</Grid>
											<Grid item xs={2} sx={{ alignItems: "left" }}>
												<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
													{moment(currentSetup?.dueDate ?? dueDate).format("L")}
												</Typography>
											</Grid>

											<Grid item xs={2} sx={{ textAlign: "right" }}>
												Valor :
											</Grid>
											<Grid item xs={3} sx={{ alignItems: "left" }}>
												<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
													{parseFloat(currentSetup?.raw_amount ?? valueSetup).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
												</Typography>
											</Grid>
										</Grid>
										<Grid container item direction="row" spacing={2} mt={1}>
											<Grid item xs={12} md={8} lg={12} xl={12}>
												<VTextField
													fullWidth
													label="Motivo do cancelamento"
													name="reason"
													disabled={isLoading}
													defaultValue={defaultReason}
													select
													onChange={(e) => setReason(e.target.value)}
												>
													<MenuItem value={"Valor incorreto"}>Valor Incorreto</MenuItem>
													<MenuItem value={"Duplicidade"}>Duplicidade</MenuItem>
													<MenuItem value={"Isenção de encargos"}>Isenção</MenuItem>
													<MenuItem value={"Desistencia"}>Desistencia</MenuItem>
													<MenuItem value={"Outros"}>Outros</MenuItem>
												</VTextField>
											</Grid>
										</Grid>
										<Grid container item direction="row" spacing={2}>
											<Grid item xs={12}>
												<VTextField
													fullWidth
													label="Descreva o motivo"
													name="note"
													disabled={isLoading}
													multiline
													rows={4}
													onChange={(e) => setNote(e.target.value)}
												></VTextField>
											</Grid>
										</Grid>
									</Box>
								</VForm>
							</Typography>
						</Box>
					</DialogContent>
					<DialogActions style={{ display: "flex", justifyContent: "center" }}>
						<Button onClick={isCloseModal} color="inherit" disableElevation variant={"contained"} startIcon={<Icon> arrow_back_icon</Icon>} disabled={isLoading}>
							Voltar
						</Button>
						{reason != "" ? (
							<Button
								color="primary"
								disableElevation
								variant={"contained"}
								startIcon={<Icon> checkIcon</Icon>}
								disabled={isLoading}
								onClick={() => {
									formRef.current?.submitForm();
								}}
							>
								{isLoading ? "Processando..." : "Confirmar"}
							</Button>
						) : (
							""
						)}
					</DialogActions>
				</Box>
			</Modal>
		</div>
	);
};
